import { useEffect, useState } from "react";

import type { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";

import { calculateBuilderCost } from "utilities/lineItem/lineItemHelper";
import { recalculateAllFields } from "utilities/lineItem/markupUtility";

import { type IMarkupLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { type ISearchBarResultResponse } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";

export interface IPreviewLineItemData {
    [key: string]: unknown;
    costItemId: number;
    title: string;
    costCodeTitle: string;
    costCodeId: number;
    description: string;
    internalNotes: string;
    quantity: number;
    unitType: string;
    unitCost: number;
    costTypes: number[];
    markupPercentage: number;
    markupAmount: number;
    markupPerUnit: number;
    markupType: MarkupColumnTypes;
    ownerPrice: number;
    builderCost: number;
    margin: number;
    profit: number;
    markedAs: number;
}

interface ILineItemSearchResult extends ISearchBarResultResponse {
    costCodeId: number;
    title: string;
    costCodeTitle: string;
    description: string;
    internalNotes: string;
    unitCost: number;
    quantity: number;
    unitType: string;
    markupType: MarkupColumnTypes;
    markupPercentage: number;
    markupAmount: number;
    markupPerUnit: number;
    costTypes: number[];
    markedAs: number;
}

export interface IPreviewLineItemProps {
    addPreviewListener?: (key: string, fn: (lineItem?: IPreviewLineItemData) => void) => void;
}

interface ILineItemPreviewProps extends IPreviewLineItemProps {
    fieldName: string;
    render: (lineItem?: IPreviewLineItemData) => React.ReactNode;
}

export const LineItemPreview: React.FC<ILineItemPreviewProps> = (props) => {
    const { fieldName, addPreviewListener } = props;
    const [lineItem, setLineItem] = useState<IPreviewLineItemData | undefined>(undefined);

    useEffect(() => {
        if (addPreviewListener) {
            addPreviewListener(fieldName, (newLineItem?: IPreviewLineItemData) => {
                setLineItem(newLineItem);
            });
        }
    }, [addPreviewListener, fieldName]);

    return <>{props.render(lineItem)}</>;
};

export function getLineItemDataFromSearchResult(
    result: ISearchBarResultResponse,
): IPreviewLineItemData {
    const lineItemResult = result as ILineItemSearchResult;
    const {
        unitCost,
        markupType,
        markupPercentage,
        markupAmount,
        markupPerUnit,
        costCodeId,
        costCodeTitle = "",
        title = "",
        description = "",
        internalNotes,
        unitType,
        costTypes,
        markedAs,
        margin,
    } = lineItemResult;
    const quantity = lineItemResult.quantity ?? 1;
    const builderCost = calculateBuilderCost(unitCost, quantity);

    const inputs = {
        unitCost,
        quantity,
        builderCost,
        ownerPrice: builderCost,
        markupType,
        markupPercent: markupPercentage,
        markupAmount,
        markupPerUnit,
        margin,
    } as IMarkupLineItem;

    const outputs = { ...recalculateAllFields(inputs), unitCost, quantity };
    const lineItemData: IPreviewLineItemData = {
        costItemId: result.id,
        costCodeId,
        costCodeTitle,
        title,
        description,
        internalNotes,
        unitType,
        costTypes,
        markedAs,
        markupPercentage: outputs.markupPercent,
        markupAmount: outputs.markupAmount,
        markupPerUnit: outputs.markupPerUnit,
        markupType: outputs.markupType,
        quantity: outputs.quantity,
        unitCost: outputs.unitCost,
        builderCost: outputs.builderCost,
        ownerPrice: outputs.ownerPrice,
        margin: outputs.margin ? outputs.margin : 0,
        profit: outputs.ownerPrice - outputs.builderCost,
    };
    return lineItemData;
}
